import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Zap, Shield, TrendingUp, ChevronRight, Copy, Check,
  Users, ArrowRight, Terminal, Lock, Eye, Bot, Cpu,
  Layers, BarChart3, Globe, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { KutroLogoFull } from '@/components/Logo';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Animated Code Rain Background ─── */
function CodeRain() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let w = canvas.width = window.innerWidth;
    let h = canvas.height = window.innerHeight;
    const cols = Math.floor(w / 16);
    const drops = Array(cols).fill(1);
    const chars = '01アイウエオカキクケコ$€£¥KOJIRU';
    const draw = () => {
      ctx.fillStyle = 'rgba(5,5,5,0.05)';
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(59,130,246,0.15)';
      ctx.font = '14px monospace';
      drops.forEach((y, i) => {
        const c = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillText(c, i * 16, y * 16);
        if (y * 16 > h && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      });
    };
    const id = setInterval(draw, 50);
    const resize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    window.addEventListener('resize', resize);
    return () => { clearInterval(id); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
}

/* ─── Glitch Text Effect ─── */
function GlitchText({ children, className = '' }) {
  return (
    <span className={`relative inline-block ${className}`}>
      <span className="relative z-10">{children}</span>
      <span className="absolute top-0 left-0 -translate-x-[2px] translate-y-[1px] text-red-500/30 z-0 select-none" aria-hidden>{children}</span>
      <span className="absolute top-0 left-0 translate-x-[2px] -translate-y-[1px] text-blue-500/30 z-0 select-none" aria-hidden>{children}</span>
    </span>
  );
}

/* ─── Terminal Typing Effect ─── */
function TerminalLine({ text, delay = 0 }) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      let i = 0;
      const iv = setInterval(() => {
        setDisplayed(text.slice(0, i + 1));
        i++;
        if (i >= text.length) { clearInterval(iv); setDone(true); }
      }, 30);
      return () => clearInterval(iv);
    }, delay);
    return () => clearTimeout(timeout);
  }, [text, delay]);
  return (
    <div className="flex items-center gap-2 font-mono text-sm">
      <span className="text-green-500">$</span>
      <span className="text-gray-300">{displayed}</span>
      {!done && <span className="w-2 h-4 bg-green-500 animate-pulse" />}
    </div>
  );
}

/* ─── Live Counter ─── */
function AnimatedCounter({ value, label }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (value === 0) return;
    const duration = 1500;
    const steps = 40;
    const inc = value / steps;
    let current = 0;
    const iv = setInterval(() => {
      current += inc;
      if (current >= value) { setCount(value); clearInterval(iv); }
      else setCount(Math.floor(current));
    }, duration / steps);
    return () => clearInterval(iv);
  }, [value]);
  return (
    <div className="text-center">
      <div className="font-mono text-3xl sm:text-4xl font-bold text-white">{count.toLocaleString()}</div>
      <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}

/* ─── Main Kojiru 5000 Page ─── */
export default function Kutro5000() {
  const [email, setEmail] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [agentLookup, setAgentLookup] = useState('');
  const [lookupResult, setLookupResult] = useState(null);

  // Extract referral from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) setReferralCode(ref);
  }, []);

  // Fetch stats and leaderboard
  useEffect(() => {
    fetch(`${API}/api/waitlist/stats`).then(r => r.json()).then(setStats).catch(() => {});
    fetch(`${API}/api/waitlist/leaderboard`).then(r => r.json()).then(d => setLeaderboard(d.leaderboard || [])).catch(() => {});
  }, []);

  const handleJoin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/waitlist/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, referred_by: referralCode || null }),
      });
      const data = await res.json();
      if (res.ok) {
        setResult(data);
        // Refresh stats
        fetch(`${API}/api/waitlist/stats`).then(r => r.json()).then(setStats);
        fetch(`${API}/api/waitlist/leaderboard`).then(r => r.json()).then(d => setLeaderboard(d.leaderboard || []));
      } else {
        alert(data.detail || 'Something went wrong');
      }
    } catch { alert('Network error'); }
    finally { setLoading(false); }
  };

  const copyLink = () => {
    if (!result) return;
    navigator.clipboard.writeText(`${window.location.origin}/kutro5000?ref=${result.agent_id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lookupPosition = async () => {
    if (!agentLookup.trim()) return;
    try {
      const res = await fetch(`${API}/api/waitlist/position/${agentLookup.trim()}`);
      if (res.ok) setLookupResult(await res.json());
      else setLookupResult({ error: 'Agent ID not found' });
    } catch { setLookupResult({ error: 'Network error' }); }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white overflow-hidden">
      <CodeRain />

      {/* ─── Nav ─── */}
      <nav className="fixed top-0 w-full z-50 border-b border-white/5 backdrop-blur-xl bg-[#030303]/80">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <KutroLogoFull size={32} />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs font-mono text-gray-600">KOJIRU_5000_v1</span>
            <a href="#join" className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-sm font-mono hover:bg-white/10 transition-colors">
              ENTER
            </a>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════════
          SECTION 1: HERO — "The Glitch"
         ═══════════════════════════════════════════════════ */}
      <section className="relative z-10 pt-32 pb-20 px-6" data-testid="kutro5000-hero">
        <div className="container mx-auto max-w-5xl text-center">
          {/* Status badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/5 mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs font-mono text-blue-400 tracking-wider">AUTONOMOUS LEDGER PROTOCOL</span>
          </motion.div>

          {/* Main headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] mb-6"
          >
            <span className="block text-gray-400 text-lg sm:text-xl font-mono font-normal mb-4 tracking-widest">THE LAST BANK YOU'LL EVER NEED IS AN</span>
            <GlitchText className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              AGENT
            </GlitchText>
          </motion.h1>

          {/* Slogan */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-6 leading-relaxed"
          >
            <span className="text-white font-semibold">Flat Fee Home Lending.</span>
            <br />
            The first Autonomous Lending Marketplace — powered by AI Agents, secured by code, settled on-chain.
          </motion.p>

          {/* Terminal animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-xl mx-auto rounded-xl border border-white/10 bg-black/60 backdrop-blur-xl p-5 text-left mb-10"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-3 h-3 rounded-full bg-red-500/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
              <div className="w-3 h-3 rounded-full bg-green-500/60" />
              <span className="text-xs font-mono text-gray-600 ml-2">kutro_agent_v1.sh</span>
            </div>
            <div className="space-y-2">
              <TerminalLine text="Initializing Autonomous Underwriting Agent..." delay={800} />
              <TerminalLine text="Scanning 1,247 data points across 3 chains..." delay={2400} />
              <TerminalLine text="Credit profile optimized. Lowest rate found: 4.2% APR" delay={4000} />
              <TerminalLine text="Loan offer generated in 4.2 seconds. Awaiting approval." delay={5600} />
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <a href="#join" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 font-semibold text-lg hover:brightness-110 transition-all group">
              Join the Kojiru 5,000 <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 2: LIVE COUNTER
         ═══════════════════════════════════════════════════ */}
      <section className="relative z-10 py-16 border-y border-white/5" data-testid="kutro5000-counter">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
            <AnimatedCounter value={stats?.total_signups || 0} label="Agents Deployed" />
            <AnimatedCounter value={stats?.spots_remaining ?? 5000} label="Spots Remaining" />
            <AnimatedCounter value={5000} label="Kojiru 5,000 Goal" />
          </div>
          {stats && (
            <div className="max-w-lg mx-auto mt-8">
              <div className="flex justify-between text-xs font-mono text-gray-600 mb-2">
                <span>{stats.total_signups.toLocaleString()} / 5,000</span>
                <span>{stats.pct_filled}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(stats.pct_filled, 100)}%` }}
                  transition={{ duration: 1.5, ease: 'easeOut' }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 3: THE AUTONOMOUS BRIDGE
         ═══════════════════════════════════════════════════ */}
      <section className="relative z-10 py-24 px-6" data-testid="kutro5000-agents">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-xs font-mono text-blue-400 tracking-widest uppercase">The Autonomous Bridge</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3">Your Agent. Your Ledger. Your Terms.</h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">We aren't just a lending platform. We are an Agentic Marketplace where AI does the heavy lifting 24/7.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Borrower Agent */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-cyan-500/20 bg-gradient-to-br from-cyan-500/5 to-transparent p-8 backdrop-blur-sm"
              data-testid="borrower-agent-card"
            >
              <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mb-5">
                <Bot className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">Borrowing Agent</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Your personal AI that constantly scans the blockchain for the lowest rates and optimizes your credit profile in real-time. No forms. No bias. Just your best offer, instantly.
              </p>
              <div className="space-y-2">
                {['Scans 1,000+ data points instantly', 'Finds lowest rates across chains', 'Manages collateral to prevent liquidation'].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <Zap className="w-3.5 h-3.5 text-cyan-400 shrink-0" /> {t}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Lender Agent */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl border border-purple-500/20 bg-gradient-to-br from-purple-500/5 to-transparent p-8 backdrop-blur-sm"
              data-testid="lender-agent-card"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center mb-5">
                <Cpu className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold mb-2">Underwriting Agent</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                Evaluates risk 24/7, manages liquidity, and executes loans with near-zero manual oversight. Your capital works while you sleep.
              </p>
              <div className="space-y-2">
                {['24/7 AI-powered risk assessment', 'Dynamic rate optimization', 'Automated capital deployment'].map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                    <Zap className="w-3.5 h-3.5 text-purple-400 shrink-0" /> {t}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 4: THE FLYWHEEL — 3 Pillars
         ═══════════════════════════════════════════════════ */}
      <section className="relative z-10 py-24 px-6 border-y border-white/5" data-testid="kutro5000-flywheel">
        <div className="container mx-auto max-w-5xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-xs font-mono text-green-400 tracking-widest uppercase">The Growth Engine</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3">Three Pillars. One Flywheel.</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield, color: 'blue',
                title: 'Trust & Security',
                mechanism: 'Immutable Audits',
                desc: 'Agents continuously monitor smart contracts for vulnerabilities and flag anomalies before they become exploits.',
              },
              {
                icon: BarChart3, color: 'green',
                title: 'Liquidity Efficiency',
                mechanism: 'Dynamic Rebalancing',
                desc: 'AI agents automate lending rate adjustments based on real-time demand — best yield for lenders, best rate for borrowers.',
              },
              {
                icon: Sparkles, color: 'purple',
                title: 'Incentive Alignment',
                mechanism: 'Agentic Rewards',
                desc: 'Borrowers who use AI agents to maintain healthy collateral ratios receive "Agent-Tier" discounts, gamifying financial responsibility.',
              },
            ].map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-2xl border border-white/5 bg-white/[0.02] p-7 backdrop-blur-sm hover:border-white/10 transition-colors"
              >
                <pillar.icon className={`w-8 h-8 text-${pillar.color}-400 mb-4`} />
                <h3 className="font-bold text-lg mb-1">{pillar.title}</h3>
                <span className={`inline-block text-xs font-mono px-2 py-0.5 rounded-full bg-${pillar.color}-500/10 text-${pillar.color}-400 border border-${pillar.color}-500/20 mb-3`}>
                  {pillar.mechanism}
                </span>
                <p className="text-sm text-gray-500 leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 5: AGENT-ZERO PERKS
         ═══════════════════════════════════════════════════ */}
      <section className="relative z-10 py-24 px-6" data-testid="kutro5000-perks">
        <div className="container mx-auto max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="text-xs font-mono text-orange-400 tracking-widest uppercase">Kojiru 5,000 Exclusive</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3">Agent-Zero Status</h2>
            <p className="text-gray-500 mt-3">The first 5,000 spots are the only ones that matter.</p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Lock, label: 'Lifetime 0% Origination Fees', desc: 'Never pay origination fees on any loan. Ever.' },
              { icon: Terminal, label: 'Early Agent Access', desc: 'Deploy your Borrowing or Underwriting Agent before public launch.' },
              { icon: Eye, label: 'Governance Rights', desc: 'Vote on AI risk parameters and platform policy from day one.' },
              { icon: Layers, label: 'Token Airdrop', desc: 'Guaranteed allocation in the KOJIRU token distribution event.' },
            ].map((perk, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-xl border border-orange-500/10 bg-orange-500/[0.02] p-5 text-center"
              >
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center mx-auto mb-3">
                  <perk.icon className="w-5 h-5 text-orange-400" />
                </div>
                <h4 className="font-semibold text-sm mb-1">{perk.label}</h4>
                <p className="text-xs text-gray-500">{perk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 6: JOIN THE WAITLIST
         ═══════════════════════════════════════════════════ */}
      <section id="join" className="relative z-10 py-24 px-6 border-t border-white/5" data-testid="kutro5000-join">
        <div className="container mx-auto max-w-lg">
          <AnimatePresence mode="wait">
            {!result ? (
              <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-bold mb-2">Deploy Your Agent</h2>
                  <p className="text-gray-500 text-sm">Secure your spot in the Kojiru 5,000. One email. One Agent ID. Zero middlemen.</p>
                </div>

                <form onSubmit={handleJoin} className="rounded-2xl border border-white/10 bg-black/40 backdrop-blur-xl p-8 space-y-5">
                  <div>
                    <label className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-2 block">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:border-blue-500/50 transition-colors placeholder:text-gray-700"
                      required
                      data-testid="waitlist-email-input"
                    />
                  </div>

                  {referralCode && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/5 border border-green-500/10">
                      <Users className="w-4 h-4 text-green-400" />
                      <span className="text-xs font-mono text-green-400">Referred by: {referralCode}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:brightness-110 font-mono text-sm tracking-wider"
                    data-testid="waitlist-join-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        DEPLOYING AGENT...
                      </span>
                    ) : (
                      <>DEPLOY AGENT <ChevronRight className="w-4 h-4 ml-1" /></>
                    )}
                  </Button>

                  <p className="text-[11px] text-gray-600 text-center">
                    By joining, you agree to Kojiru's <Link to="/privacy" className="text-blue-500 hover:underline">Privacy Policy</Link>. No spam. Agents only.
                  </p>
                </form>
              </motion.div>
            ) : (
              /* ─── Success State ─── */
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="rounded-2xl border border-green-500/20 bg-green-500/[0.03] backdrop-blur-xl p-8 text-center"
                data-testid="waitlist-success"
              >
                <div className="w-16 h-16 rounded-2xl bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold mb-2">
                  {result.already_joined ? 'Welcome Back, Agent.' : 'Agent Deployed.'}
                </h3>
                <p className="text-gray-400 text-sm mb-6">You are #{result.position} in the Kojiru 5,000.</p>

                {/* Agent ID Card */}
                <div className="rounded-xl border border-white/10 bg-black/40 p-5 mb-6">
                  <div className="text-xs font-mono text-gray-500 uppercase mb-2">Your Agent ID</div>
                  <div className="text-2xl font-mono font-bold text-blue-400 tracking-wider">{result.agent_id}</div>
                </div>

                {/* Referral Link */}
                <div className="rounded-xl border border-white/10 bg-black/40 p-5 mb-6">
                  <div className="text-xs font-mono text-gray-500 uppercase mb-3">Deploy Your Referral Agent</div>
                  <p className="text-xs text-gray-500 mb-3">Share your link. Each referral moves you up 10 positions.</p>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/kutro5000?ref=${result.agent_id}`}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-xs font-mono text-gray-400"
                      data-testid="referral-link-input"
                    />
                    <Button
                      onClick={copyLink}
                      variant="outline"
                      className="border-white/10 px-3"
                      data-testid="copy-referral-btn"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-gray-600">
                  Flat Fee Home Lending. The bridge is opening.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 7: LEADERBOARD
         ═══════════════════════════════════════════════════ */}
      <section className="relative z-10 py-24 px-6 border-t border-white/5" data-testid="kutro5000-leaderboard">
        <div className="container mx-auto max-w-2xl">
          <div className="text-center mb-12">
            <span className="text-xs font-mono text-purple-400 tracking-widest uppercase">Agent Leaderboard</span>
            <h2 className="text-2xl font-bold mt-3">Top Recruiters</h2>
            <p className="text-gray-500 text-sm mt-2">Deploy your referral agent. Rise through the ranks.</p>
          </div>

          {leaderboard.length > 0 ? (
            <div className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/5 text-xs font-mono text-gray-600 uppercase">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Agent ID</div>
                <div className="col-span-3 text-right">Referrals</div>
                <div className="col-span-3 text-right">Position</div>
              </div>
              {leaderboard.map((entry, i) => (
                <div key={entry.agent_id} className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors items-center">
                  <div className="col-span-1 font-mono text-sm font-bold text-gray-500">{i + 1}</div>
                  <div className="col-span-5 font-mono text-sm text-blue-400">{entry.agent_id}</div>
                  <div className="col-span-3 text-right font-mono text-sm text-green-400">{entry.referral_count}</div>
                  <div className="col-span-3 text-right font-mono text-sm text-gray-400">#{entry.position}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 rounded-2xl border border-white/5 bg-white/[0.02]">
              <Globe className="w-8 h-8 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-600">No referrals yet. Be the first to deploy your agent.</p>
            </div>
          )}

          {/* Position Lookup */}
          <div className="mt-8 rounded-xl border border-white/5 bg-white/[0.02] p-5">
            <div className="text-xs font-mono text-gray-500 uppercase mb-3">Check Your Position</div>
            <div className="flex gap-2">
              <input
                value={agentLookup}
                onChange={(e) => setAgentLookup(e.target.value)}
                placeholder="Enter Agent ID (e.g. AGT-X7K9M2)"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm font-mono focus:outline-none focus:border-blue-500/50 placeholder:text-gray-700"
                data-testid="agent-lookup-input"
              />
              <Button onClick={lookupPosition} variant="outline" className="border-white/10 font-mono text-sm" data-testid="agent-lookup-btn">
                SCAN
              </Button>
            </div>
            {lookupResult && (
              <div className="mt-3 p-3 rounded-lg bg-white/[0.03] border border-white/5 font-mono text-sm">
                {lookupResult.error ? (
                  <span className="text-red-400">{lookupResult.error}</span>
                ) : (
                  <div className="flex justify-between items-center">
                    <span className="text-blue-400">{lookupResult.agent_id}</span>
                    <span className="text-gray-400">Position #{lookupResult.position} | {lookupResult.referral_count} referrals</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════
          SECTION 8: FINAL CTA
         ═══════════════════════════════════════════════════ */}
      <section className="relative z-10 py-24 px-6 border-t border-white/5" data-testid="kutro5000-cta">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Bridge Is Opening.</h2>
            <p className="text-lg text-gray-400 mb-2">
              <span className="text-white font-semibold">Flat Fee Home Lending.</span>
            </p>
            <p className="text-gray-500 mb-8">Don't get left on the other side.</p>
            <a href="#join" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 font-semibold text-lg hover:brightness-110 transition-all group">
              Secure Your Spot <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="relative z-10 border-t border-white/5 py-10 px-6">
        <div className="container mx-auto max-w-5xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <KutroLogoFull size={24} />
          </div>
          <div className="flex items-center gap-6 text-xs text-gray-600">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <span className="font-mono">&copy; 2026 Kojiru Technologies, Inc.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
